import React, { Fragment } from 'react'
import { NumberInput } from '@structura/ui'
import { CrossSection, MaterialFactorsDisplay, SMBeamModel } from '../../types'
import { UseMaterialStateResult } from '../../hooks/useMaterialState'
import { useTranslation } from '@structura/i18n'

interface MaterialFormPanelProps
{
  model: SMBeamModel
  setModel: React.Dispatch<React.SetStateAction<SMBeamModel>>
  materialState: UseMaterialStateResult
}

const MaterialFormPanel: React.FC<MaterialFormPanelProps> = ({
  model,
  setModel,
  materialState
}) =>
{
  const {
    currentMaterial,
    nationalAnnex,
    setNationalAnnex,
    selectedSteelGrade,
    setSelectedSteelGrade,
    selectedSteelSubgrade,
    setSelectedSteelSubgrade,
    steelSubgrades,
    steelBands,
    steelThickness,
    setSteelThickness,
    selectedConcreteClass,
    setSelectedConcreteClass,
    selectedReinfClass,
    setSelectedReinfClass,
    selectedPrestressClass,
    setSelectedPrestressClass,
    timberKind,
    selectedTimberClass,
    setSelectedTimberClass,
    timberOptions,
    timberKmod,
    setTimberKmod,
    timberKmodTable,
    timberLoadDuration,
    setTimberLoadDuration,
    timberServiceClass,
    setTimberServiceClass,
    timberCreepFactor,
    concreteRelativeHumidity,
    setConcreteRelativeHumidity,
    concreteNotionalSize,
    setConcreteNotionalSize,
    concreteLoadingAge,
    setConcreteLoadingAge,
    concreteCementType,
    setConcreteCementType,
    concreteCreepCoefficient,
    concreteEffectiveModulus,
    steelInfoLine,
    concreteInfoLine,
    timberInfoLine,
    showMaterialParams,
    setShowMaterialParams,
    checkLateralTorsionalBuckling,
    setCheckLateralTorsionalBuckling,
    resolvedDensity,
    calculateResolvedSelfWeight,
    materialFactorsDisplay,
    fetchMaterialDetails,
    resolvingMaterial,
    materialDetails
  } = materialState

  const { t } = useTranslation()

  return (
    <div>
      <NationalAnnexSelector
        nationalAnnex={nationalAnnex}
        onChange={setNationalAnnex}
      />

      <SelectedMaterialBanner
        currentMaterial={currentMaterial}
        timberKind={timberKind}
        steelInfoLine={steelInfoLine}
        concreteInfoLine={concreteInfoLine}
        timberInfoLine={timberInfoLine}
      />

      <MaterialClassSelectors
        currentMaterial={currentMaterial}
        selectedSteelGrade={selectedSteelGrade}
        setSelectedSteelGrade={setSelectedSteelGrade}
        selectedSteelSubgrade={selectedSteelSubgrade}
        setSelectedSteelSubgrade={setSelectedSteelSubgrade}
        steelSubgrades={steelSubgrades}
        steelBands={steelBands}
        steelThickness={steelThickness}
        setSteelThickness={setSteelThickness}
        selectedConcreteClass={selectedConcreteClass}
        setSelectedConcreteClass={setSelectedConcreteClass}
        selectedReinfClass={selectedReinfClass}
        setSelectedReinfClass={setSelectedReinfClass}
        selectedPrestressClass={selectedPrestressClass}
        setSelectedPrestressClass={setSelectedPrestressClass}
        timberKind={timberKind}
        selectedTimberClass={selectedTimberClass}
        setSelectedTimberClass={setSelectedTimberClass}
        timberOptions={timberOptions}
        timberKmod={timberKmod}
        setTimberKmod={setTimberKmod}
        timberKmodTable={timberKmodTable}
        timberLoadDuration={timberLoadDuration}
        setTimberLoadDuration={setTimberLoadDuration}
        timberServiceClass={timberServiceClass}
        setTimberServiceClass={setTimberServiceClass}
        timberCreepFactor={timberCreepFactor}
        concreteRelativeHumidity={concreteRelativeHumidity}
        setConcreteRelativeHumidity={setConcreteRelativeHumidity}
        concreteNotionalSize={concreteNotionalSize}
        setConcreteNotionalSize={setConcreteNotionalSize}
        concreteLoadingAge={concreteLoadingAge}
        setConcreteLoadingAge={setConcreteLoadingAge}
        concreteCementType={concreteCementType}
        setConcreteCementType={setConcreteCementType}
        concreteCreepCoefficient={concreteCreepCoefficient}
        concreteEffectiveModulus={concreteEffectiveModulus}
      />

      <MaterialDetailsToggle
        showMaterialParams={showMaterialParams}
        setShowMaterialParams={setShowMaterialParams}
      />

      {showMaterialParams && (
        <MaterialDetailsCard
          currentMaterial={currentMaterial}
          timberKind={timberKind}
          selectedSteelGrade={selectedSteelGrade}
          selectedConcreteClass={selectedConcreteClass}
          selectedTimberClass={selectedTimberClass}
          fetchMaterialDetails={fetchMaterialDetails}
          resolvingMaterial={resolvingMaterial}
          materialDetails={materialDetails}
        />
      )}

      <SelfWeightInfo
        calculateResolvedSelfWeight={calculateResolvedSelfWeight}
        resolvedDensity={resolvedDensity}
        model={model}
      />

      <MaterialFactorsCard
        currentMaterial={currentMaterial}
        timberKind={timberKind}
        timberKmod={timberKmod}
        factors={materialFactorsDisplay}
      />

      <div className="mb-3">
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="checkLateralTorsionalBuckling"
            checked={checkLateralTorsionalBuckling}
            onChange={(event) => setCheckLateralTorsionalBuckling(event.target.checked)}
          />
          <label className="form-check-label" htmlFor="checkLateralTorsionalBuckling">
            {t('beam.materials.checkLtb')}
          </label>
        </div>
        <small className="text-muted d-block mt-1">{t('beam.materials.checkLtbHint')}</small>
      </div>

      <ManualPropertiesCard
        model={model}
        setModel={setModel}
      />

      <MaterialFactorNotes currentMaterial={currentMaterial} />
    </div>
  )
}

export default MaterialFormPanel

interface NationalAnnexSelectorProps
{
  nationalAnnex: 'Standard' | 'Norwegian'
  onChange: React.Dispatch<React.SetStateAction<'Standard' | 'Norwegian'>>
}

const NationalAnnexSelector: React.FC<NationalAnnexSelectorProps> = ({ nationalAnnex, onChange }) => {
  const { t } = useTranslation()
  return (
    <div className="mb-4">
      <label className="form-label fw-bold">{t('beam.materials.nationalAnnex')}</label>
      <select
        className="form-select"
        value={nationalAnnex}
        onChange={(event) => onChange(event.target.value as 'Standard' | 'Norwegian')}
      >
        <option value="Norwegian">{t('beam.materials.annexNorwegian')}</option>
        <option value="Standard">{t('beam.materials.annexStandard')}</option>
      </select>
    </div>
  )
}

interface SelectedMaterialBannerProps
{
  currentMaterial: string
  timberKind: 'glulam' | 'solid'
  steelInfoLine: string
  concreteInfoLine: string
  timberInfoLine: string
}

const SelectedMaterialBanner: React.FC<SelectedMaterialBannerProps> = ({
  currentMaterial,
  timberKind,
  steelInfoLine,
  concreteInfoLine,
  timberInfoLine
}) => {
  const { t } = useTranslation()
  const getMaterialLabel = (): string => {
    if (currentMaterial === 'steel') return t('beam.materials.labelSteel')
    if (currentMaterial === 'concrete') return t('beam.materials.labelConcrete')
    if (currentMaterial === 'timber') return timberKind === 'glulam' ? t('beam.materials.labelGlulam') : t('beam.materials.labelSolidWood')
    return t('beam.materials.labelUnknown')
  }

  const getMaterialIcon = (): string => {
    if (currentMaterial === 'steel') return 'bi-wrench'
    if (currentMaterial === 'concrete') return 'bi-bricks'
    if (currentMaterial === 'timber') return timberKind === 'glulam' ? 'bi-layers' : 'bi-tree'
    return 'bi-question'
  }

  const getInfoLine = (): string => {
    if (currentMaterial === 'steel') return steelInfoLine
    if (currentMaterial === 'concrete') return concreteInfoLine
    if (currentMaterial === 'timber') return timberInfoLine
    return ''
  }

  return (
    <div className="mb-3">
      <label className="form-label fw-bold">{t('beam.materials.selectedMaterial')}</label>
      <div className="beam-alert-info d-flex align-items-center mb-0" role="alert">
        <i className={`bi ${getMaterialIcon()} fs-4 me-3`}></i>
        <div>
          <div className="fw-bold">{getMaterialLabel()}</div>
          <small className="text-muted">{getInfoLine()}</small>
        </div>
      </div>
      <small className="text-muted mt-1 d-block">
        <i className="bi bi-info-circle me-1"></i>
        {t('beam.materials.changeHint')}
      </small>
    </div>
  )
}

interface MaterialClassSelectorsProps
{
  currentMaterial: string
  selectedSteelGrade: string
  setSelectedSteelGrade: React.Dispatch<React.SetStateAction<any>>
  selectedSteelSubgrade: string
  setSelectedSteelSubgrade: React.Dispatch<React.SetStateAction<string>>
  steelSubgrades: string[]
  steelBands: number[]
  steelThickness: number | ''
  setSteelThickness: React.Dispatch<React.SetStateAction<number | ''>>
  selectedConcreteClass: string
  setSelectedConcreteClass: React.Dispatch<React.SetStateAction<any>>
  selectedReinfClass: string
  setSelectedReinfClass: React.Dispatch<React.SetStateAction<any>>
  selectedPrestressClass: string
  setSelectedPrestressClass: React.Dispatch<React.SetStateAction<any>>
  timberKind: 'glulam' | 'solid'
  selectedTimberClass: string
  setSelectedTimberClass: React.Dispatch<React.SetStateAction<string>>
  timberOptions: any[]
  timberKmod: number
  setTimberKmod: React.Dispatch<React.SetStateAction<number>>
  timberKmodTable: Record<string, number>
  timberLoadDuration: 'permanent' | 'long-term' | 'medium-term' | 'short-term' | 'instantaneous'
  setTimberLoadDuration: React.Dispatch<React.SetStateAction<'permanent' | 'long-term' | 'medium-term' | 'short-term' | 'instantaneous'>>
  timberServiceClass: 1 | 2 | 3
  setTimberServiceClass: React.Dispatch<React.SetStateAction<1 | 2 | 3>>
  timberCreepFactor: number
  concreteRelativeHumidity: number
  setConcreteRelativeHumidity: React.Dispatch<React.SetStateAction<number>>
  concreteNotionalSize: number
  setConcreteNotionalSize: React.Dispatch<React.SetStateAction<number>>
  concreteLoadingAge: number
  setConcreteLoadingAge: React.Dispatch<React.SetStateAction<number>>
  concreteCementType: 'S' | 'N' | 'R'
  setConcreteCementType: React.Dispatch<React.SetStateAction<'S' | 'N' | 'R'>>
  concreteCreepCoefficient: number
  concreteEffectiveModulus: number
}

const MaterialClassSelectors: React.FC<MaterialClassSelectorsProps> = ({
  currentMaterial,
  selectedSteelGrade,
  setSelectedSteelGrade,
  selectedSteelSubgrade,
  setSelectedSteelSubgrade,
  steelSubgrades,
  steelBands,
  steelThickness,
  setSteelThickness,
  selectedConcreteClass,
  setSelectedConcreteClass,
  selectedReinfClass,
  setSelectedReinfClass,
  selectedPrestressClass,
  setSelectedPrestressClass,
  timberKind,
  selectedTimberClass,
  setSelectedTimberClass,
  timberOptions,
  timberKmod,
  timberKmodTable,
  timberLoadDuration,
  setTimberLoadDuration,
  timberServiceClass,
  setTimberServiceClass,
  timberCreepFactor,
  concreteRelativeHumidity,
  setConcreteRelativeHumidity,
  concreteNotionalSize,
  setConcreteNotionalSize,
  concreteLoadingAge,
  setConcreteLoadingAge,
  concreteCementType,
  setConcreteCementType,
  concreteCreepCoefficient,
  concreteEffectiveModulus
}) => {
  const { t } = useTranslation()
  // kmod preview per duration comes from the backend table; '…' until it loads (no hardcoding).
  const kmodLabel = (duration: string): string => {
    const value = timberKmodTable[duration]
    return typeof value === 'number' ? value.toFixed(2) : '…'
  }
  return (
  <div className="mb-3">
    <label className="form-label fw-bold">{t('beam.materials.classOrGrade')}</label>

    {currentMaterial === 'steel' && (
      <div className="row g-2">
        <div className="col-md-3">
          <label className="form-label">{t('beam.materials.steelGrade')}</label>
          <select className="form-select" value={selectedSteelGrade} onChange={(event) => setSelectedSteelGrade(event.target.value as any)}>
            <option value="S235">S235</option>
            <option value="S275">S275</option>
            <option value="S355">S355</option>
            <option value="S420">S420</option>
            <option value="S460">S460</option>
          </select>
        </div>
        <div className="col-md-3">
          <label className="form-label">{t('beam.materials.subgrade')}</label>
          <select className="form-select" value={selectedSteelSubgrade} onChange={(event) => setSelectedSteelSubgrade(event.target.value)}>
            {steelSubgrades.length > 0 ? (
              steelSubgrades.map((subgrade) => (
                <option key={subgrade} value={subgrade}>{subgrade || '—'}</option>
              ))
            ) : (
              <option value="">—</option>
            )}
          </select>
        </div>
        <div className="col-md-3">
          <label className="form-label text-nowrap">{t('beam.materials.thicknessLe')}</label>
          <select className="form-select" value={String(steelThickness)} onChange={(event) => setSteelThickness(parseFloat(event.target.value))}>
            {steelBands.length === 0 ? (
              <option value="">—</option>
            ) : (
              steelBands.map((band) => (
                <option key={band} value={String(band)}>{band}</option>
              ))
            )}
          </select>
        </div>
      </div>
    )}

    {currentMaterial === 'concrete' && (
      <>
        <div className="row g-2">
          <div className="col-md-4">
            <label className="form-label">{t('beam.materials.concrete')}</label>
            <select className="form-select" value={selectedConcreteClass} onChange={(event) => setSelectedConcreteClass(event.target.value as any)}>
              {[
                'C12/15','C16/20','C20/25','C25/30','C30/37','C35/45','C40/50','C45/55','C50/60','C55/67','C60/75','C70/85','C80/95','C90/105'
              ].map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">{t('beam.materials.reinforcement')}</label>
            <select className="form-select" value={selectedReinfClass} onChange={(event) => setSelectedReinfClass(event.target.value as any)}>
              <optgroup label={t('beam.materials.reinfNorwegian')}>
                <option value="B500NC">{t('beam.materials.b500nc')}</option>
                <option value="B500NB">{t('beam.materials.b500nb')}</option>
              </optgroup>
              <optgroup label={t('beam.materials.reinfEurocode')}>
                <option value="B500C">{t('beam.materials.b500c')}</option>
                <option value="B500B">{t('beam.materials.b500b')}</option>
                <option value="B500A">{t('beam.materials.b500a')}</option>
              </optgroup>
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">{t('beam.materials.prestressingPT')}</label>
            <select className="form-select" value={selectedPrestressClass} onChange={(event) => setSelectedPrestressClass(event.target.value as any)}>
              <option value="Y1860">Y1860 (fpk = 1860 MPa)</option>
              <option value="Y1770">Y1770 (fpk = 1770 MPa)</option>
            </select>
          </div>
        </div>

        <div className="mt-3">
          <label className="form-label fw-bold">
            <i className="bi bi-clock-history me-1"></i>
            {t('beam.materials.creepShrinkageParams')}
          </label>
          <div className="row g-2">
            <div className="col-md-3">
              <label className="form-label">{t('beam.materials.relativeHumidity')}</label>
              <select className="form-select" value={concreteRelativeHumidity} onChange={(event) => setConcreteRelativeHumidity(parseFloat(event.target.value))}>
                <option value="40">{t('beam.materials.rh40')}</option>
                <option value="50">{t('beam.materials.rh50')}</option>
                <option value="60">{t('beam.materials.rh60')}</option>
                <option value="70">{t('beam.materials.rh70')}</option>
                <option value="80">{t('beam.materials.rh80')}</option>
                <option value="90">{t('beam.materials.rh90')}</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">{t('beam.materials.notionalSize')}</label>
              <NumberInput
                value={concreteNotionalSize}
                onCommit={setConcreteNotionalSize}
              />
              <small className="text-muted">h₀ = 2·Ac/u</small>
            </div>
            <div className="col-md-3">
              <label className="form-label">{t('beam.materials.loadingAge')}</label>
              <select className="form-select" value={concreteLoadingAge} onChange={(event) => setConcreteLoadingAge(parseFloat(event.target.value))}>
                <option value="7">{t('beam.materials.age7')}</option>
                <option value="14">{t('beam.materials.age14')}</option>
                <option value="28">{t('beam.materials.age28')}</option>
                <option value="56">{t('beam.materials.age56')}</option>
                <option value="90">{t('beam.materials.age90')}</option>
                <option value="365">{t('beam.materials.age365')}</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">{t('beam.materials.cementType')}</label>
              <select className="form-select" value={concreteCementType} onChange={(event) => setConcreteCementType(event.target.value as 'S' | 'N' | 'R')}>
                <option value="S">{t('beam.materials.cementS')}</option>
                <option value="N">{t('beam.materials.cementN')}</option>
                <option value="R">{t('beam.materials.cementR')}</option>
              </select>
              <small className="text-muted">{t('beam.materials.forShrinkage')}</small>
            </div>
          </div>
          <div className="beam-alert-info mt-2 mb-0">
            <div className="d-flex justify-content-between align-items-center">
              <span>
                <strong>{t('beam.materials.creepCoefficient')}</strong> {concreteCreepCoefficient.toFixed(2)}
              </span>
              <span>
                <strong>Ec,eff:</strong> {concreteEffectiveModulus > 0 ? (concreteEffectiveModulus / 1e9).toFixed(1) : '—'} GPa
              </span>
            </div>
            <small className="text-muted d-block mt-1">
              {t('beam.materials.ecEffNote')}
            </small>
          </div>
        </div>
      </>
    )}

    {currentMaterial === 'timber' && (
      <>
        <div className="row g-2">
          <div className="col-md-4">
            <label className="form-label">{t('beam.materials.timberClass')}</label>
            <select className="form-select" value={selectedTimberClass} onChange={(event) => setSelectedTimberClass(event.target.value)}>
              {timberOptions.length > 0 ? (
                timberOptions
                  .filter((option) => option.kind === (timberKind === 'glulam' ? 'glulam' : 'solid'))
                  .map((option) => (
                    <option key={option.id} value={option.class}>{option.class}</option>
                  ))
              ) : (
                timberKind === 'glulam'
                  ? (<Fragment><option value="GL24h">GL24h</option><option value="GL28h">GL28h</option></Fragment>)
                  : (<Fragment><option value="C24">C24</option><option value="C30">C30</option></Fragment>)
              )}
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">{t('beam.materials.kmodLoadDuration')}</label>
            <select className="form-select" value={timberLoadDuration} onChange={(event) => setTimberLoadDuration(event.target.value as typeof timberLoadDuration)} title={t('beam.materials.kmodDerivedNote')}>
              <option value="permanent">{t('beam.materials.durationPermanent', { k: kmodLabel('permanent') })}</option>
              <option value="long-term">{t('beam.materials.durationLong', { k: kmodLabel('long-term') })}</option>
              <option value="medium-term">{t('beam.materials.durationMedium', { k: kmodLabel('medium-term') })}</option>
              <option value="short-term">{t('beam.materials.durationShort', { k: kmodLabel('short-term') })}</option>
              <option value="instantaneous">{t('beam.materials.durationInstant', { k: kmodLabel('instantaneous') })}</option>
            </select>
            <small className="text-muted">{t('beam.materials.kmodValue', { k: timberKmod.toFixed(2) })}</small>
          </div>
          <div className="col-md-4">
            <label className="form-label">{t('beam.materials.serviceClass')}</label>
            <select className="form-select" value={timberServiceClass} onChange={(event) => setTimberServiceClass(parseInt(event.target.value) as 1 | 2 | 3)}>
              <option value="1">{t('beam.materials.serviceClass1')}</option>
              <option value="2">{t('beam.materials.serviceClass2')}</option>
              <option value="3">{t('beam.materials.serviceClass3')}</option>
            </select>
          </div>
        </div>

        <div className="beam-alert-info mt-2 mb-0">
          <div className="d-flex justify-content-between align-items-center">
            <span>
              <strong>{t('beam.materials.creepFactorKdef')}</strong> {timberCreepFactor.toFixed(2)}
            </span>
            <span className="text-muted">
              {t('beam.materials.ec5Table32')}
            </span>
          </div>
          <small className="text-muted d-block mt-1">
            {t('beam.materials.finalDeflection', { factor: (1 + timberCreepFactor).toFixed(2) })}
          </small>
        </div>
      </>
    )}
  </div>
  )
}

interface MaterialDetailsToggleProps
{
  showMaterialParams: boolean
  setShowMaterialParams: React.Dispatch<React.SetStateAction<boolean>>
}

const MaterialDetailsToggle: React.FC<MaterialDetailsToggleProps> = ({ showMaterialParams, setShowMaterialParams }) => {
  const { t } = useTranslation()
  return (
    <div className="d-flex align-items-center gap-2 mb-3">
      <div className="form-check form-switch m-0">
        <input
          className="form-check-input"
          type="checkbox"
          id="toggleMaterialParams"
          checked={showMaterialParams}
          onChange={() => setShowMaterialParams((previous) => !previous)}
        />
        <label className="form-check-label" htmlFor="toggleMaterialParams">{t('beam.materials.showDetails')}</label>
      </div>
    </div>
  )
}

interface MaterialDetailsCardProps
{
  currentMaterial: string
  timberKind: 'glulam' | 'solid'
  selectedSteelGrade: string
  selectedConcreteClass: string
  selectedTimberClass: string
  fetchMaterialDetails: () => Promise<void>
  resolvingMaterial: boolean
  materialDetails: any
}

const MaterialDetailsCard: React.FC<MaterialDetailsCardProps> = ({
  currentMaterial,
  timberKind,
  selectedSteelGrade,
  selectedConcreteClass,
  selectedTimberClass,
  fetchMaterialDetails: _fetchMaterialDetails,
  resolvingMaterial,
  materialDetails
}) => {
  const { t } = useTranslation()
  return (
  <div className="card mb-3">
    <div className="card-header bg-light">
      <strong>{t('beam.materials.materialProperties')}</strong>
      {resolvingMaterial && <span className="ms-2 text-muted small">{t('beam.materials.loadingShort')}</span>}
    </div>
    <div className="card-body">
      {!materialDetails ? (
        <div className="text-center text-muted py-3">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">{t('beam.status.loading')}</span>
          </div>
          <p className="mb-0">{t('beam.materials.loadingFromDb')}</p>
        </div>
      ) : materialDetails.error ? (
        <div className="alert alert-danger">{materialDetails.error}</div>
      ) : (
        <div className="row">
          {currentMaterial === 'steel' && (
            <SteelMaterialDetails
              materialDetails={materialDetails}
              selectedSteelGrade={selectedSteelGrade}
            />
          )}

          {currentMaterial === 'timber' && (
            <TimberMaterialDetails
              materialDetails={materialDetails}
              selectedTimberClass={selectedTimberClass}
              timberKind={timberKind}
            />
          )}

          {currentMaterial === 'concrete' && (
            <ConcreteMaterialDetails
              materialDetails={materialDetails}
              selectedConcreteClass={selectedConcreteClass}
            />
          )}

          {materialDetails.codeVersion && (
            <div className="col-12 mt-2">
              <small className="text-muted">
                <strong>{t('beam.materials.standard')}</strong> {materialDetails.codeVersion}
                {materialDetails.nationalAnnexYear && ` | NA: ${materialDetails.nationalAnnexYear}`}
              </small>
            </div>
          )}
        </div>
      )}
    </div>
  </div>
  )
}

interface SteelMaterialDetailsProps
{
  materialDetails: any
  selectedSteelGrade: string
}

const SteelMaterialDetails: React.FC<SteelMaterialDetailsProps> = ({ materialDetails, selectedSteelGrade }) => {
  const { t } = useTranslation()
  return (
  <>
    <div className="col-md-6">
      <h6 className="text-primary mb-2">{t('beam.materials.material')}</h6>
      <table className="table table-sm table-borderless">
        <tbody>
          <tr>
            <td className="text-muted">{t('beam.materials.grade')}</td>
            <td className="fw-bold">{materialDetails.grade || selectedSteelGrade}</td>
          </tr>
          {materialDetails.subgrade && (
            <tr>
              <td className="text-muted">{t('beam.materials.subgrade')}:</td>
              <td className="fw-bold">{materialDetails.subgrade}</td>
            </tr>
          )}
          {materialDetails.productForm && (
            <tr>
              <td className="text-muted">{t('beam.materials.productForm')}</td>
              <td>{materialDetails.productForm}</td>
            </tr>
          )}
          {materialDetails.thicknessMax && (
            <tr>
              <td className="text-muted">{t('beam.materials.thicknessRange')}</td>
              <td>≤ {materialDetails.thicknessMax} mm</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
    <div className="col-md-6">
      <h6 className="text-primary mb-2">{t('beam.materials.properties')}</h6>
      <table className="table table-sm table-borderless">
        <tbody>
          {materialDetails.yieldStrength && (
            <tr>
              <td className="text-muted">{t('beam.materials.yieldStrength')}</td>
              <td className="fw-bold">{materialDetails.yieldStrength} MPa</td>
            </tr>
          )}
          {materialDetails.fyd && (
            <tr>
              <td className="text-muted">{t('beam.materials.designStrengthFyd')}</td>
              <td className="fw-bold text-success">{materialDetails.fyd.toFixed(1)} MPa</td>
            </tr>
          )}
          {materialDetails.ultimateStrengthMin && (
            <tr>
              <td className="text-muted">{t('beam.materials.ultimateStrengthFu')}</td>
              <td>{materialDetails.ultimateStrengthMin} MPa</td>
            </tr>
          )}
          {materialDetails.elasticModulus && (
            <tr>
              <td className="text-muted">{t('beam.materials.elasticModulusE')}</td>
              <td>{(materialDetails.elasticModulus / 1e9).toFixed(0)} GPa</td>
            </tr>
          )}
          {materialDetails.density && (
            <tr>
              <td className="text-muted">{t('beam.materials.density')}</td>
              <td>{materialDetails.density} kg/m³</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </>
  )
}

interface TimberMaterialDetailsProps
{
  materialDetails: any
  timberKind: 'glulam' | 'solid'
  selectedTimberClass: string
}

const TimberMaterialDetails: React.FC<TimberMaterialDetailsProps> = ({ materialDetails, timberKind, selectedTimberClass }) => {
  const { t } = useTranslation()
  return (
  <>
    <div className="col-md-6">
      <h6 className="text-success mb-2">{t('beam.materials.material')}</h6>
      <table className="table table-sm table-borderless">
        <tbody>
          <tr>
            <td className="text-muted">{t('beam.materials.type')}</td>
            <td className="fw-bold">{timberKind === 'solid' ? t('beam.materials.solidTimber') : t('beam.materials.gluedLaminated')}</td>
          </tr>
          <tr>
            <td className="text-muted">{t('beam.materials.class')}</td>
            <td className="fw-bold">{materialDetails.class || selectedTimberClass}</td>
          </tr>
          {materialDetails.kmod && (
            <tr>
              <td className="text-muted">kmod:</td>
              <td>{materialDetails.kmod.toFixed(2)}</td>
            </tr>
          )}
          {materialDetails.gammaM && (
            <tr>
              <td className="text-muted">γM:</td>
              <td>{materialDetails.gammaM.toFixed(2)}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
    <div className="col-md-6">
      <h6 className="text-success mb-2">{t('beam.materials.strengths')}</h6>
      <table className="table table-sm table-borderless">
        <tbody>
          {materialDetails.fb_d && (
            <tr>
              <td className="text-muted">{t('beam.materials.bendingFbd')}</td>
              <td className="fw-bold text-success">{materialDetails.fb_d.toFixed(1)} MPa</td>
            </tr>
          )}
          {materialDetails.ft0_d && (
            <tr>
              <td className="text-muted">{t('beam.materials.tensionFt0d')}</td>
              <td>{materialDetails.ft0_d.toFixed(1)} MPa</td>
            </tr>
          )}
          {materialDetails.fc0_d && (
            <tr>
              <td className="text-muted">{t('beam.materials.compressionFc0d')}</td>
              <td>{materialDetails.fc0_d.toFixed(1)} MPa</td>
            </tr>
          )}
          {materialDetails.fv_d && (
            <tr>
              <td className="text-muted">{t('beam.materials.shearFvd')}</td>
              <td>{materialDetails.fv_d.toFixed(2)} MPa</td>
            </tr>
          )}
          {materialDetails.e0Mean && (
            <tr>
              <td className="text-muted">{t('beam.materials.eModulusE0')}</td>
              <td>{(materialDetails.e0Mean / 1e9).toFixed(1)} GPa</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </>
  )
}

interface ConcreteMaterialDetailsProps
{
  materialDetails: any
  selectedConcreteClass: string
}

const ConcreteMaterialDetails: React.FC<ConcreteMaterialDetailsProps> = ({ materialDetails, selectedConcreteClass }) => {
  const { t } = useTranslation()
  return (
  <>
    <div className="col-12">
      <h6 className="text-secondary mb-2">{t('beam.materials.concrete')}</h6>
    </div>
    <div className="col-md-6">
      <table className="table table-sm table-borderless">
        <tbody>
          <tr>
            <td className="text-muted">{t('beam.materials.class')}</td>
            <td className="fw-bold">{materialDetails.class || selectedConcreteClass}</td>
          </tr>
          {materialDetails.fck && (
            <tr>
              <td className="text-muted">{t('beam.materials.characteristicFck')}</td>
              <td className="fw-bold">{materialDetails.fck} MPa</td>
            </tr>
          )}
          {materialDetails.fcd && (
            <tr>
              <td className="text-muted">{t('beam.materials.designCompressiveFcd')}</td>
              <td className="fw-bold text-success">{materialDetails.fcd.toFixed(1)} MPa</td>
            </tr>
          )}
          {materialDetails.fctd && (
            <tr>
              <td className="text-muted">{t('beam.materials.designTensileFctd')}</td>
              <td>{materialDetails.fctd.toFixed(2)} MPa</td>
            </tr>
          )}
          {materialDetails.gammaC && (
            <tr>
              <td className="text-muted">γC:</td>
              <td>{materialDetails.gammaC.toFixed(2)}</td>
            </tr>
          )}
          {materialDetails.alphaCC && (
            <tr>
              <td className="text-muted">αcc:</td>
              <td>{materialDetails.alphaCC.toFixed(2)}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
    <div className="col-md-6">
      <table className="table table-sm table-borderless">
        <tbody>
          {materialDetails.elasticModulus && (
            <tr>
              <td className="text-muted">{t('beam.materials.elasticModulusEcm')}</td>
              <td>{(materialDetails.elasticModulus / 1e9).toFixed(1)} GPa</td>
            </tr>
          )}
          {materialDetails.density && (
            <tr>
              <td className="text-muted">{t('beam.materials.density')}</td>
              <td>{materialDetails.density} kg/m³</td>
            </tr>
          )}
          <tr>
            <td className="text-muted">{t('beam.materials.ultimateStrainEcu3')}</td>
            <td>3.5 ‰</td>
          </tr>
          <tr>
            <td className="text-muted">{t('beam.materials.strainAtPeakEc3')}</td>
            <td>{materialDetails.fck <= 50 ? '1.75' : '2.0 + 0.085(fck-50)^0.53'} ‰</td>
          </tr>
        </tbody>
      </table>
    </div>

    {materialDetails.reinforcement && (
      <>
        <div className="col-12 mt-3">
          <h6 className="text-danger mb-2">{t('beam.materials.reinforcementSteel')}</h6>
        </div>
        <div className="col-md-6">
          <table className="table table-sm table-borderless">
            <tbody>
              <tr>
                <td className="text-muted">{t('beam.materials.grade')}</td>
                <td className="fw-bold">{materialDetails.reinforcement.grade}</td>
              </tr>
              {materialDetails.reinforcement.ductilityClass && (
                <tr>
                  <td className="text-muted">{t('beam.materials.ductilityClass')}</td>
                  <td>{materialDetails.reinforcement.ductilityClass}</td>
                </tr>
              )}
              {materialDetails.reinforcement.fyk && (
                <tr>
                  <td className="text-muted">{t('beam.materials.characteristicFyk')}</td>
                  <td className="fw-bold">{materialDetails.reinforcement.fyk} MPa</td>
                </tr>
              )}
              {materialDetails.reinforcement.fyd && (
                <tr>
                  <td className="text-muted">{t('beam.materials.designStrengthFyd')}</td>
                  <td className="fw-bold text-success">{materialDetails.reinforcement.fyd.toFixed(1)} MPa</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="col-md-6">
          <table className="table table-sm table-borderless">
            <tbody>
              {materialDetails.reinforcement.ftk && (
                <tr>
                  <td className="text-muted">{t('beam.materials.tensileStrengthFtk')}</td>
                  <td>{materialDetails.reinforcement.ftk} MPa</td>
                </tr>
              )}
              {materialDetails.reinforcement.k && (
                <tr>
                  <td className="text-muted">{t('beam.materials.ratioKFtkFyk')}</td>
                  <td>{materialDetails.reinforcement.k.toFixed(2)}</td>
                </tr>
              )}
              {materialDetails.reinforcement.fyd && materialDetails.reinforcement.elasticModulus && (
                <tr>
                  <td className="text-muted">{t('beam.materials.yieldStrainEyd')}</td>
                  <td className="fw-bold">{((materialDetails.reinforcement.fyd * 1e6 / materialDetails.reinforcement.elasticModulus) * 1000).toFixed(2)} ‰</td>
                </tr>
              )}
              {materialDetails.reinforcement.epsilonUk && (
                <tr>
                  <td className="text-muted">{t('beam.materials.strainAtMaxEuk')}</td>
                  <td>{materialDetails.reinforcement.epsilonUk.toFixed(1)} %</td>
                </tr>
              )}
              {materialDetails.reinforcement.gammaS && (
                <tr>
                  <td className="text-muted">γS:</td>
                  <td>{materialDetails.reinforcement.gammaS.toFixed(2)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </>
    )}

    {materialDetails.prestressing && (
      <>
        <div className="col-12 mt-3">
          <h6 className="text-warning mb-2">{t('beam.materials.prestressingSteel')}</h6>
        </div>
        <div className="col-md-6">
          <table className="table table-sm table-borderless">
            <tbody>
              <tr>
                <td className="text-muted">{t('beam.materials.grade')}</td>
                <td className="fw-bold">{materialDetails.prestressing.grade}</td>
              </tr>
              {materialDetails.prestressing.productForm && (
                <tr>
                  <td className="text-muted">{t('beam.materials.productForm')}</td>
                  <td>{materialDetails.prestressing.productForm}</td>
                </tr>
              )}
              {materialDetails.prestressing.fpk && (
                <tr>
                  <td className="text-muted">{t('beam.materials.tensileStrengthFpk')}</td>
                  <td className="fw-bold">{materialDetails.prestressing.fpk} MPa</td>
                </tr>
              )}
              {materialDetails.prestressing.fpd && (
                <tr>
                  <td className="text-muted">{t('beam.materials.designStrengthFpd')}</td>
                  <td className="fw-bold text-success">{materialDetails.prestressing.fpd.toFixed(1)} MPa</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="col-md-6">
          <table className="table table-sm table-borderless">
            <tbody>
              {materialDetails.prestressing.fp01k && (
                <tr>
                  <td className="text-muted">{t('beam.materials.proof01Fp01k')}</td>
                  <td>{materialDetails.prestressing.fp01k} MPa</td>
                </tr>
              )}
              {materialDetails.prestressing.k && (
                <tr>
                  <td className="text-muted">{t('beam.materials.ratioKFpkFp01k')}</td>
                  <td>{materialDetails.prestressing.k.toFixed(2)}</td>
                </tr>
              )}
              {materialDetails.prestressing.fp01k && materialDetails.prestressing.elasticModulus && (
                <tr>
                  <td className="text-muted">{t('beam.materials.elasticStrainEp01k')}</td>
                  <td>{((materialDetails.prestressing.fp01k * 1e6 / materialDetails.prestressing.elasticModulus) * 100).toFixed(2)} %</td>
                </tr>
              )}
              {materialDetails.prestressing.fpk && materialDetails.prestressing.elasticModulus && (
                <tr>
                  <td className="text-muted">{t('beam.materials.strainAtFpkEpk')}</td>
                  <td>{((materialDetails.prestressing.fpk * 1e6 / materialDetails.prestressing.elasticModulus) * 100).toFixed(2)} %</td>
                </tr>
              )}
              {materialDetails.prestressing.epsilonUk && (
                <tr>
                  <td className="text-muted">{t('beam.materials.ultimateStrainEuk')}</td>
                  <td>{materialDetails.prestressing.epsilonUk.toFixed(1)} %</td>
                </tr>
              )}
              {materialDetails.prestressing.relaxationClass && (
                <tr>
                  <td className="text-muted">{t('beam.materials.relaxationClass')}</td>
                  <td>{materialDetails.prestressing.relaxationClass}</td>
                </tr>
              )}
              {materialDetails.prestressing.gammaS && (
                <tr>
                  <td className="text-muted">γS:</td>
                  <td>{materialDetails.prestressing.gammaS.toFixed(2)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </>
    )}
  </>
  )
}

interface SelfWeightInfoProps
{
  calculateResolvedSelfWeight: (crossSection: CrossSection) => number
  resolvedDensity: number
  model: SMBeamModel
}

// Reads the resolved density/self-weight so this panel shows exactly the load the analysis carries.
const SelfWeightInfo: React.FC<SelfWeightInfoProps> = ({ calculateResolvedSelfWeight, resolvedDensity, model }) => {
  const { t } = useTranslation()
  return (
  <div className="mb-3">
    <label className="form-label fw-bold">{t('beam.materials.selfWeight')}</label>
    <div className="beam-alert-info">
      <div className="d-flex justify-content-between">
        <span>{t('beam.materials.currentSelfWeight')}</span>
        <strong>{calculateResolvedSelfWeight(model.crossSection).toFixed(4)} kN/m</strong>
      </div>
      <small className="text-muted">
        {t('beam.materials.selfWeightCalc', { density: resolvedDensity, area: (model.crossSection.area * 1e4).toFixed(1) })}
      </small>
    </div>
  </div>
  )
}

interface MaterialFactorsCardProps
{
  currentMaterial: string
  timberKind: 'glulam' | 'solid'
  timberKmod: number
  factors: MaterialFactorsDisplay
}

const MaterialFactorsCard: React.FC<MaterialFactorsCardProps> = ({ currentMaterial, timberKind, timberKmod, factors }) => {
  const { t } = useTranslation()
  // Render a backend-resolved value, or '…' until the /design payload has loaded. SLS/ALS material
  // factors are the Eurocode-definitional 1.00 (no material partial factor in those situations).
  const f2 = (value: number | undefined): string => typeof value === 'number' ? value.toFixed(2) : '…'
  const f0 = (value: number | undefined): string => typeof value === 'number' ? value.toFixed(0) : '…'
  return (
  <div className="mb-3">
    <label className="form-label fw-bold">{t('beam.materials.partialFactors')}</label>
    <div className="card">
      <div className="card-body">
        {currentMaterial === 'steel' && (
          <>
            <div className="mb-2">
              <strong>{t('beam.materials.ulsSteel')}</strong>
              <ul className="mb-0 mt-1">
                <li>γM0 = {f2(factors.steelGammaM0)} <small className="text-muted">{t('beam.materials.noteCrossSectionResistance')}</small></li>
                <li>γM1 = {f2(factors.steelGammaM1)} <small className="text-muted">{t('beam.materials.noteInstabilityResistance')}</small></li>
                <li>γM2 = {f2(factors.steelGammaM2)} <small className="text-muted">{t('beam.materials.noteTensionConnections')}</small></li>
              </ul>
              <div className="alert alert-light mt-2 mb-0">
                <small>
                  <em>{t('beam.materials.noteSteelFactorsSame')}</em>
                </small>
              </div>
            </div>
            <div className="mb-2">
              <strong>{t('beam.materials.sls')}</strong>
              <ul className="mb-0 mt-1">
                <li>γM = 1.00</li>
              </ul>
            </div>
            <div>
              <strong>{t('beam.materials.als')}</strong>
              <ul className="mb-0 mt-1">
                <li>γM = 1.00</li>
              </ul>
            </div>
          </>
        )}

        {currentMaterial === 'concrete' && (
          <>
            <div className="mb-2">
              <strong>{t('beam.materials.ulsConcrete')}</strong>
              <ul className="mb-0 mt-1">
                <li>γC = {f2(factors.concreteGammaC)} <small className="text-muted">{t('beam.materials.noteConcrete')}</small></li>
                <li>γS = {f2(factors.concreteGammaS)} <small className="text-muted">{t('beam.materials.noteReinfSteel')}</small></li>
                <li>γP = {f2(factors.concretePrestressGammaS)} <small className="text-muted">{t('beam.materials.notePrestressSteel')}</small></li>
                <li>αcc = {f2(factors.concreteAlphaCC)} <small className="text-muted">{t('beam.materials.noteAlphaCC')}</small></li>
              </ul>
            </div>
            <div className="mb-2">
              <strong>{t('beam.materials.sls')}</strong>
              <ul className="mb-0 mt-1">
                <li>γC = 1.00 <small className="text-muted">{t('beam.materials.noteConcrete')}</small></li>
                <li>γS = 1.00 <small className="text-muted">{t('beam.materials.noteReinfSteel')}</small></li>
                <li>γP = 1.00 <small className="text-muted">{t('beam.materials.notePrestressSteel')}</small></li>
              </ul>
            </div>
            <div className="mb-3">
              <strong>{t('beam.materials.als')}</strong>
              <ul className="mb-0 mt-1">
                <li>γC = 1.00 <small className="text-muted">{t('beam.materials.noteConcreteReduced')}</small></li>
                <li>γS = 1.00 <small className="text-muted">{t('beam.materials.noteReinfSteel')}</small></li>
                <li>γP = 1.00 <small className="text-muted">{t('beam.materials.notePrestressSteel')}</small></li>
              </ul>
            </div>
            <div className="alert alert-light mb-0">
              <strong>{t('beam.materials.referenceProps')}</strong>
              <ul className="mb-0 mt-1">
                <li>{t('beam.materials.refReinforcement', { fy: f0(factors.reinforcementFyMpa), es: f0(factors.reinforcementEsGpa) })}</li>
                <li>{t('beam.materials.refPrestressing', { fpk: f0(factors.prestressFpkMpa), ep: f0(factors.prestressEpGpa) })}</li>
              </ul>
            </div>
          </>
        )}

        {currentMaterial === 'timber' && (
          <>
            <div className="mb-2">
              <strong>{t('beam.materials.ulsTimber')}</strong>
              <ul className="mb-0 mt-1">
                <li>γM = {f2(factors.timberGammaM)} <small className="text-muted">{timberKind === 'solid' ? t('beam.materials.noteSolidSawn') : t('beam.materials.noteGlulam')}</small></li>
                <li>kmod = {timberKmod.toFixed(2)} <small className="text-muted">{t('beam.materials.noteFromDurationService')}</small></li>
              </ul>
              <div className="alert alert-light mt-2 mb-2">
                <small>
                  {t('beam.materials.timberDesignStrengthNote')}
                </small>
              </div>
            </div>
            <div className="mb-2">
              <strong>{t('beam.materials.sls')}</strong>
              <ul className="mb-0 mt-1">
                <li>γM = 1.00</li>
              </ul>
            </div>
            <div>
              <strong>{t('beam.materials.als')}</strong>
              <ul className="mb-0 mt-1">
                <li>γM = 1.00</li>
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  </div>
  )
}

interface ManualPropertiesCardProps
{
  model: SMBeamModel
  setModel: React.Dispatch<React.SetStateAction<SMBeamModel>>
}

const ManualPropertiesCard: React.FC<ManualPropertiesCardProps> = ({ model, setModel }) => {
  const { t } = useTranslation()
  return (
  <div>
    <label className="form-label fw-bold">{t('beam.materials.manualProperties')}</label>
    <div className="mb-2">
      <label className="form-label">{t('beam.materials.youngsModulus')}</label>
      <NumberInput
        value={(model.spans[0]?.E || 200e9) / 1e9}
        onCommit={(parsed) => {
          const E = parsed * 1e9
          setModel((previous) => ({
            ...previous,
            spans: previous.spans.map((span) => ({ ...span, E }))
          }))
        }}
      />
    </div>
  </div>
  )
}

interface MaterialFactorNotesProps
{
  currentMaterial: string
}

const MaterialFactorNotes: React.FC<MaterialFactorNotesProps> = ({ currentMaterial }) => {
  const { t } = useTranslation()
  return (
  <div className="alert alert-secondary mt-4">
    <small>
      {currentMaterial === 'steel' && (
        <em>{t('beam.materials.notesSteel')}</em>
      )}
      {currentMaterial === 'concrete' && (
        <>
          <strong>{t('beam.materials.notesConcreteAlphaCC')}</strong><br />
          • {t('beam.materials.notesConcreteNorwegian')}<br />
          • {t('beam.materials.notesConcreteStandard')}<br />
          <em className="text-muted">{t('beam.materials.notesConcreteOther')}</em>
        </>
      )}
      {currentMaterial === 'timber' && (
        <em>{t('beam.materials.notesTimber')}</em>
      )}
    </small>
  </div>
  )
}
